import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    // Exigido pelo backend em toda requisição que altera dados (defesa contra CSRF)
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Sessão expirada ou troca de senha pendente: avisa o AuthContext, que redireciona.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const url = error.config?.url || '';
    if (status === 401 && !url.startsWith('/auth/login')) {
      window.dispatchEvent(new CustomEvent('auth:expired'));
    } else if (status === 403 && code === 'PASSWORD_CHANGE_REQUIRED') {
      window.dispatchEvent(new CustomEvent('auth:password-change'));
    }
    return Promise.reject(error);
  }
);

/** Mensagem legível de um erro da API (o backend devolve { message }). */
export function apiErrorMessage(error, fallback = 'Algo deu errado. Tente novamente.') {
  return error?.response?.data?.message || fallback;
}

// ═══════════════════════════════════════════════════════════════
// AUTH & ADMIN API
// ═══════════════════════════════════════════════════════════════

export const authService = {
  login: async (username, password) => (await api.post('/auth/login', { username, password })).data,
  logout: async () => { await api.post('/auth/logout'); },
  demoStatus: async () => (await api.get('/auth/demo')).data,
  demoLogin: async () => (await api.post('/auth/demo')).data,
  me: async () => (await api.get('/auth/me')).data,
  changePassword: async (currentPassword, newPassword) =>
    (await api.post('/auth/change-password', { currentPassword, newPassword })).data,
  updateGoals: async (goals) => (await api.put('/auth/goals', goals)).data,
  // Primeiro acesso: perfil, foto e passo do assistente
  updateProfile: async (profile) => (await api.put('/auth/me', profile)).data,
  uploadAvatar: async (file) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post('/auth/me/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  removeAvatar: async () => (await api.delete('/auth/me/avatar')).data,
  // { step, goal, done, tourSeen, trailsIntroSeen, trailsHowSeen,
  //   tour: { uso: { step, done }, edicao: { step, done } } }: o tour é parcial (só muda o que vier)
  updateOnboarding: async (data) => (await api.put('/auth/onboarding', data)).data,
};

// Notificações do sininho e da página /notificacoes
export const notificationService = {
  list: async ({ category, cursor, limit } = {}) =>
    (await api.get('/notifications', { params: { category: category || undefined, cursor: cursor || undefined, limit } })).data,
  unreadCount: async () => (await api.get('/notifications/unread-count')).data,
  mark: async (id, read) => { await api.patch(`/notifications/${id}`, { read }); },
  markAllRead: async (category) => (await api.post('/notifications/read-all', { category: category || null })).data,
};

export const adminService = {
  listUsers: async () => (await api.get('/admin/users')).data,
  createUser: async (user) => (await api.post('/admin/users', user)).data,
  updateUser: async (id, user) => (await api.put(`/admin/users/${id}`, user)).data,
  resetPassword: async (id) => (await api.post(`/admin/users/${id}/reset-password`)).data,
  usernameAvailable: async (username, ignoreRequest) =>
    (await api.get('/admin/users/available', { params: { username, ignoreRequest } })).data.available,
  // Solicitações de cadastro
  listRequests: async () => (await api.get('/admin/access-requests')).data,
  pendingRequests: async () => (await api.get('/admin/access-requests/count')).data.pending,
  approveRequest: async (id, user) => (await api.post(`/admin/access-requests/${id}/approve`, user)).data,
  rejectRequest: async (id) => { await api.post(`/admin/access-requests/${id}/reject`); },
  undoReject: async (id) => { await api.post(`/admin/access-requests/${id}/undo`); },
  // Moderação de comentários
  removeComment: async (id, reason, notifyAuthor) => {
    await api.delete(`/admin/comments/${id}`, { data: { reason, notifyAuthor } });
  },
  restoreComment: async (id) => { await api.post(`/admin/comments/${id}/restore`); },
};

// ═══════════════════════════════════════════════════════════════
// TRILHAS
// ═══════════════════════════════════════════════════════════════

export const trailService = {
  catalog: async (params) => (await api.get('/trails', { params })).data,
  today: async () => (await api.get('/trails/today')).data || null,
  suggestions: async (goal) => (await api.get('/trails/suggestions', { params: { goal } })).data,
  onboardingTags: async (trail) => (await api.get('/trails/onboarding-tags', { params: { trail } })).data,
  detail: async (slug) => (await api.get(`/trails/${slug}`)).data,
  // n: 0 = antes de começar, 1..N = etapas, 99 = reta final
  stage: async (slug, n) => (await api.get(`/trails/${slug}/stages/${n}`)).data,
  plan: async (slug) => (await api.get(`/trails/${slug}/plan`)).data,
  icsUrl: (slug) => `${API_BASE_URL}/trails/${slug}/plan.ics`,
  // { hours, weekdays: '1,2,3', examDate }
  preview: async (slug, params) => (await api.get(`/trails/${slug}/plan-preview`, { params })).data,
  exam: async (slug) => (await api.get(`/trails/${slug}/exam`)).data,
  checklist: async (slug, item, done) => (await api.put(`/trails/${slug}/checklist`, { item, done })).data,
  // { hoursPerDay, weekdays: [1..7], examDate, examTime }
  enroll: async (slug, data) => (await api.post(`/trails/${slug}/enroll`, data)).data,
  updateEnrollment: async (slug, data, replan = false) =>
    (await api.put(`/trails/${slug}/enrollment`, data, { params: { replan } })).data,
  // { done, page, unit, step, value, highlight: { page, text }, removeHighlight }
  progress: async (resourceId, data) => (await api.post(`/trails/resources/${resourceId}/progress`, data)).data,
  attempt: async (resourceId) => (await api.post(`/trails/resources/${resourceId}/attempt`)).data,
  // Editor (admin)
  create: async (data) => (await api.post('/trails', data)).data,
  forEdit: async (slug) => (await api.get(`/trails/${slug}/edit`)).data,
  save: async (slug, doc) => (await api.put(`/trails/${slug}`, doc)).data,
  remove: async (slug) => { await api.delete(`/trails/${slug}`); },
  courseTree: async () => (await api.get('/trails/editor/courses')).data,
  categories: async (course) => (await api.get('/trails/editor/categories', { params: { course } })).data,
  uploadPdf: async (file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('subfolder', 'trilhas');
    return (await api.post('/pdfs/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
};

// ═══════════════════════════════════════════════════════════════
// ARTIGOS
// ═══════════════════════════════════════════════════════════════

export const articleService = {
  // params: { tab, q, tag, course, sort, page, size }
  list: async (params) => (await api.get('/articles', { params })).data,
  featured: async () => (await api.get('/articles/featured')).data || null,
  sidebar: async () => (await api.get('/articles/sidebar')).data,
  highlights: async (course, limit = 3) => (await api.get('/articles/highlights', { params: { course, limit } })).data,
  forTopic: async (topicId) => (await api.get(`/topics/${topicId}/articles`)).data,
  read: async (slug) => (await api.get(`/articles/${slug}`)).data,
  forEdit: async (id) => (await api.get(`/articles/${id}/edit`)).data,
  create: async (article) => (await api.post('/articles', article)).data,
  update: async (id, article) => (await api.put(`/articles/${id}`, article)).data,
  publish: async (id) => (await api.post(`/articles/${id}/publish`)).data,
  unpublish: async (id) => (await api.post(`/articles/${id}/unpublish`)).data,
  remove: async (id) => { await api.delete(`/articles/${id}`); },
  uploadCover: async (file) => {
    const form = new FormData();
    form.append('file', file);
    return (await api.post('/articles/covers', form, { headers: { 'Content-Type': 'multipart/form-data' } })).data.url;
  },
  toggleSave: async (id) => (await api.post(`/articles/${id}/save`)).data,
  toggleLike: async (id) => (await api.post(`/articles/${id}/like`)).data,
  progress: async (id, progress, read) => (await api.put(`/articles/${id}/progress`, { progress, read })).data,
  share: async (id, channel) => (await api.post(`/articles/${id}/share`, { channel })).data,
  // "Gerar post com IA" no Compartilhar
  sharePost: async (id, voice) => (await api.post(`/articles/${id}/share-post`, { voice })).data.post,
  // Comentários
  comments: async (id, params) => (await api.get(`/articles/${id}/comments`, { params })).data,
  comment: async (id, body, parentId) => (await api.post(`/articles/${id}/comments`, { body, parentId })).data,
  editComment: async (commentId, body) => { await api.put(`/comments/${commentId}`, { body }); },
  deleteComment: async (commentId) => { await api.delete(`/comments/${commentId}`); },
  likeComment: async (commentId) => (await api.post(`/comments/${commentId}/like`)).data,
  reportComment: async (commentId, reason) => { await api.post(`/comments/${commentId}/report`, { reason }); },
};

export const tagService = {
  search: async (q) => (await api.get('/tags', { params: { q } })).data,
  following: async () => (await api.get('/tags/following')).data,
  appearances: async (names) => (await api.get('/tags/appearances', { params: { names: names.join(',') } })).data,
  get: async (slug) => (await api.get(`/tags/${slug}`)).data,
  toggleFollow: async (slug) => (await api.post(`/tags/${slug}/follow`)).data,
};

/** Rotas sem login: artigo público e solicitação de cadastro. */
export const publicService = {
  medal: async (token) => (await api.get(`/public/medals/${token}`)).data,
  // Artigos públicos mais recentes: { items, total }
  articles: async (limit = 3, page = 0) => (await api.get('/public/articles', { params: { limit, page } })).data,
  article: async (slug) => (await api.get(`/public/articles/${slug}`)).data,
  share: async (slug, channel) => (await api.post(`/public/articles/${slug}/share`, { channel })).data,
  courses: async () => (await api.get('/public/courses')).data,
  trails: async () => (await api.get('/public/trails')).data,
  requestAccess: async (request) => (await api.post('/public/access-requests', request)).data,
};

// ═══════════════════════════════════════════════════════════════
// COURSES API
// ═══════════════════════════════════════════════════════════════

export const courseService = {
  // Página Cursos: fases com progresso e a última aula vista
  overview: async () => (await api.get('/courses/overview')).data,

  // Get all courses
  getAllCourses: async () => {
    const response = await api.get('/courses');
    return response.data;
  },

  // Get course by slug
  getCourseBySlug: async (slug, includeTopics = false) => {
    const response = await api.get(`/courses/${slug}`, { params: { includeTopics } });
    return response.data;
  },

  // Get course by ID
  getCourseById: async (id, includeTopics = false) => {
    const response = await api.get(`/courses/id/${id}`, { params: { includeTopics } });
    return response.data;
  },

  // Create course
  createCourse: async (course) => {
    const response = await api.post('/courses', course);
    return response.data;
  },

  // Update course
  updateCourse: async (id, course) => {
    const response = await api.put(`/courses/${id}`, course);
    return response.data;
  },

  // Update course config
  updateConfig: async (id, config) => {
    const response = await api.patch(`/courses/${id}/config`, config);
    return response.data;
  },

  // Delete course
  deleteCourse: async (id) => {
    const response = await api.delete(`/courses/${id}`);
    return response.data;
  },

  // Get topics by course
  getTopicsByCourse: async (slug, includeLessons = false) => {
    const response = await api.get(`/courses/${slug}/topics`, { params: { includeLessons } });
    return response.data;
  },

  // Create topic in course
  createTopicInCourse: async (slug, topic) => {
    const response = await api.post(`/courses/${slug}/topics`, topic);
    return response.data;
  },

  // Count courses
  countCourses: async () => {
    const response = await api.get('/courses/count');
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════
// QUESTIONS API
// ═══════════════════════════════════════════════════════════════

export const questionService = {
  // Buscar todas as questões (filtradas por curso se courseSlug fornecido)
  getAllQuestions: async (courseSlug = null) => {
    const params = courseSlug ? { courseSlug } : {};
    const response = await api.get('/questions', { params });
    return response.data;
  },

  // Buscar questão por ID
  getQuestionById: async (id) => {
    const response = await api.get(`/questions/${id}`);
    return response.data;
  },

  // Contar questões (filtradas por curso se courseSlug fornecido)
  countQuestions: async (courseSlug = null) => {
    const params = courseSlug ? { courseSlug } : {};
    const response = await api.get('/questions/count', { params });
    return response.data;
  },

  // Contar questões por estilo (filtradas por curso se courseSlug fornecido)
  countByStyle: async (courseSlug = null) => {
    const params = courseSlug ? { courseSlug } : {};
    const response = await api.get('/questions/count-by-style', { params });
    return response.data;
  },

  // Contar questões por categoria e estilo (filtradas por curso se courseSlug fornecido)
  countByCategoryAndStyle: async (courseSlug = null) => {
    const params = courseSlug ? { courseSlug } : {};
    const response = await api.get('/questions/count-by-category-and-style', { params });
    return response.data;
  },

  // Buscar por categoria
  getQuestionsByCategory: async (categoryId) => {
    const response = await api.get(`/questions/category/${categoryId}`);
    return response.data;
  },

  // Buscar todas as categorias (filtradas por curso se courseSlug fornecido)
  getCategories: async (courseSlug = null) => {
    const params = courseSlug ? { courseSlug } : {};
    const response = await api.get('/categories', { params });
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════
// EXAMS API
// ═══════════════════════════════════════════════════════════════

export const examService = {
  createExam: async (examData) => (await api.post('/exams', examData)).data,
  getExamById: async (id) => (await api.get(`/exams/${id}`)).data,
  getState: async (id) => (await api.get(`/exams/${id}/state`)).data,
  startExam: async (id) => (await api.post(`/exams/${id}/start`)).data,
  // Salvamento automático: { answers, flagged, currentIndex, remainingSeconds }
  saveProgress: async (id, progress) => (await api.put(`/exams/${id}/progress`, progress)).data,
  // Finaliza e corrige no servidor; devolve o resultado completo
  completeExam: async (id, progress) => (await api.post(`/exams/${id}/complete`, progress || {})).data,
  getResult: async (id) => (await api.get(`/exams/${id}/result`)).data,
  discardExam: async (id) => { await api.delete(`/exams/${id}`); },
  getCompletedExams: async (courseSlug) => (await api.get('/exams/completed', { params: { courseSlug } })).data,
  getPendingExams: async (courseSlug) => (await api.get('/exams/pending', { params: { courseSlug } })).data,
  getStats: async (courseSlug) => (await api.get('/exams/stats', { params: { courseSlug } })).data,
  countExams: async (courseSlug) => (await api.get('/exams/count', { params: { courseSlug } })).data,
};

// ═══════════════════════════════════════════════════════════════
// GAMIFICATION API
// ═══════════════════════════════════════════════════════════════

export const gamificationService = {
  // Página Conquistas: nível, medalhas, maestria por fase, regras de XP
  getPage: async (courseSlug) => (await api.get(`/gamification/${courseSlug}`)).data,
  // Barra superior: { level, unseen }
  getSummary: async (courseSlug) => (await api.get(`/gamification/${courseSlug}/summary`)).data,
  // scope: course | global
  shareMedal: async (courseSlug, key) => (await api.post(`/gamification/${courseSlug}/achievements/${key}/share`)).data,
  getRanking: async (courseSlug, scope = 'course') =>
    (await api.get(`/gamification/${courseSlug}/ranking`, { params: { scope } })).data,
  markSeen: async (courseSlug) => { await api.post(`/gamification/${courseSlug}/achievements/seen`); },
};

// ═══════════════════════════════════════════════════════════════
// IA (uma rota por tarefa; o contexto sai do servidor pelo id da aula/tópico)
// ═══════════════════════════════════════════════════════════════

/** Erro de IA com código (AI_DAILY_LIMIT, AI_NOT_CONFIGURED...) para os estados honestos da tela. */
export function aiErrorInfo(error) {
  const data = error?.response?.data || error?.data || {};
  const status = error?.response?.status;
  const code = data.code || (typeof error?.code === 'string' && /^(AI_|DEMO_|FORBIDDEN)/.test(error.code) ? error.code : null);
  // Erro interno sem código de IA: mensagem genérica (nada de SQL ou stack na tela)
  const generic = !code && (!status || status >= 500);
  return {
    code: code || 'AI_UNAVAILABLE',
    message: generic ? 'Não consegui responder agora.' : (data.message || error?.message || 'Não consegui responder agora.'),
  };
}

export const aiService = {
  status: async () => (await api.get('/ai/status')).data,

  /**
   * Tutor em streaming (SSE via fetch). onDelta(texto) a cada pedaço; resolve com {usageId, keySource, stopped}.
   * signal: AbortController.signal do botão Parar.
   */
  tutorStream: async ({ lessonId, topicId, messages, selection }, { onDelta, signal } = {}) => {
    const res = await fetch(`${API_BASE_URL}/ai/tutor/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/event-stream' },
      body: JSON.stringify({ lessonId, topicId, messages, selection }),
      signal,
    });
    if (!res.ok || !res.body) {
      let data = {};
      try { data = await res.json(); } catch { /* sem corpo */ }
      if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:expired'));
      const err = new Error(data.message || 'Não consegui responder agora.');
      err.code = data.code || (res.status === 403 ? 'FORBIDDEN' : 'AI_UNAVAILABLE');
      throw err;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done = null;
    const handle = (block) => {
      let event = 'message';
      const data = [];
      block.split('\n').forEach((line) => {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
      });
      if (!data.length) return;
      const payload = JSON.parse(data.join('\n'));
      if (event === 'delta') onDelta?.(payload.text);
      else if (event === 'done') done = payload;
      else if (event === 'error') {
        const err = new Error(payload.message);
        err.code = payload.code;
        throw err;
      }
    };
    try {
      for (;;) {
        const { value, done: finished } = await reader.read();
        if (finished) break;
        buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
        let idx;
        while ((idx = buffer.indexOf('\n\n')) >= 0) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (block.trim()) handle(block);
        }
      }
      if (buffer.trim()) handle(buffer);
    } catch (e) {
      if (e.name === 'AbortError') return { stopped: true };
      throw e;
    }
    return done || { stopped: true };
  },
  feedback: async (usageId, value) => { await api.post('/ai/feedback', { usageId, value }); },

  reviewNext: async (body) => (await api.post('/ai/review/next', body)).data,
  reviewGrade: async (body) => (await api.post('/ai/review/grade', body)).data,

  questions: async (body) => (await api.post('/ai/questions', body)).data,
  queueQuestions: async (topicId, questions) => (await api.post('/ai/questions/queue', { topicId, questions })).data,
  examNow: async (topicId, questions) => (await api.post('/ai/questions/exam', { topicId, questions })).data,

  draftFlashcards: async (body) => (await api.post('/ai/flashcards/draft', body)).data,
  saveFlashcards: async (body) => (await api.post('/ai/flashcards', body)).data,

  analyzeError: async (errorId) => (await api.post(`/ai/errors/${errorId}/analyze`)).data,

  lessonDraft: async (lessonId, part, count) => (await api.post(`/ai/lessons/${lessonId}/draft/${part}`, { count })).data,
  publishLessonDraft: async (lessonId, body) => (await api.post(`/ai/lessons/${lessonId}/publish`, body)).data,

  // Minha conta › Inteligência artificial
  myKey: async () => (await api.get('/me/ai-key')).data,
  saveMyKey: async (body) => (await api.put('/me/ai-key', body)).data,
  deleteMyKey: async () => { await api.delete('/me/ai-key'); },
  testMyKey: async () => (await api.post('/me/ai-key/test')).data,

  // Super admin › Inteligência artificial
  settings: async () => (await api.get('/admin/ai/settings')).data,
  saveSettings: async (body) => (await api.put('/admin/ai/settings', body)).data,
  testConnection: async () => (await api.post('/admin/ai/test')).data,
  usage: async () => (await api.get('/admin/ai/usage')).data,
};

/** Revisão por IA dos artigos públicos. */
export const articleReviewService = {
  review: async (articleId, publish = false) => (await api.post(`/articles/${articleId}/review`, { publish })).data,
  current: async (articleId) => (await api.get(`/articles/${articleId}/review`)).data,
  updateFinding: async (articleId, reviewId, findingId, state, justification) =>
    (await api.patch(`/articles/${articleId}/review/${reviewId}/findings/${findingId}`, { state, justification })).data,
  // Admin
  queue: async (status, q) => (await api.get('/admin/article-reviews', { params: { status, q } })).data,
  pendingCount: async () => (await api.get('/admin/article-reviews/count')).data.pending,
  detail: async (id) => (await api.get(`/admin/article-reviews/${id}`)).data,
  approve: async (id, comment, decisions) => (await api.post(`/admin/article-reviews/${id}/approve`, { comment, decisions })).data,
  reject: async (id, comment, decisions) => (await api.post(`/admin/article-reviews/${id}/reject`, { comment, decisions })).data,
};

/** Narração: voz por pessoa, áudio por tópico ou aula, gerado em segundo plano. */
export const ttsService = {
  status: async () => (await api.get('/tts/status')).data,
  setVoice: async (voice) => (await api.put('/tts/voice', { voice })).data,
  topic: async (id) => (await api.post(`/tts/topics/${id}`)).data,
  lesson: async (id) => (await api.post(`/tts/lessons/${id}`)).data,
  job: async (key) => (await api.get(`/tts/jobs/${key}`)).data,
};

// ═══════════════════════════════════════════════════════════════
// TOPICS API
// ═══════════════════════════════════════════════════════════════

export const topicService = {
  // Get all topics (optionally filtered by course)
  getAllTopics: async (includeLessons = false, courseSlug = null) => {
    const params = { includeLessons };
    if (courseSlug) {
      params.courseSlug = courseSlug;
    }
    const response = await api.get('/topics', { params });
    return response.data;
  },

  // Get topic by ID
  getTopicById: async (id, includeLessons = true) => {
    const response = await api.get(`/topics/${id}`, { params: { includeLessons } });
    return response.data;
  },

  // Create topic
  createTopic: async (topic) => {
    const response = await api.post('/topics', topic);
    return response.data;
  },

  // Update topic
  updateTopic: async (id, topic) => {
    const response = await api.put(`/topics/${id}`, topic);
    return response.data;
  },

  // Update topic artifacts
  updateArtifacts: async (id, artifacts) => {
    const response = await api.patch(`/topics/${id}/artifacts`, artifacts);
    return response.data;
  },

  // Delete topic
  deleteTopic: async (id) => {
    const response = await api.delete(`/topics/${id}`);
    return response.data;
  },

  // Count topics
  countTopics: async () => {
    const response = await api.get('/topics/count');
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════
// LESSONS API
// ═══════════════════════════════════════════════════════════════

export const lessonService = {
  // Get all lessons
  getAllLessons: async () => {
    const response = await api.get('/lessons');
    return response.data;
  },

  // Get lessons by topic
  getLessonsByTopicId: async (topicId) => {
    const response = await api.get(`/lessons/topic/${topicId}`);
    return response.data;
  },

  // Get lesson by ID
  getLessonById: async (id) => {
    const response = await api.get(`/lessons/${id}`);
    return response.data;
  },

  // Create lesson
  createLesson: async (lesson, topicId) => {
    const response = await api.post('/lessons', lesson, { params: { topicId } });
    return response.data;
  },

  // Update lesson
  updateLesson: async (id, lesson) => {
    const response = await api.put(`/lessons/${id}`, lesson);
    return response.data;
  },

  // Update lesson content
  updateContent: async (id, content) => {
    const response = await api.patch(`/lessons/${id}/content`, content);
    return response.data;
  },

  // Delete lesson
  deleteLesson: async (id) => {
    const response = await api.delete(`/lessons/${id}`);
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════
// ERROR NOTEBOOK API
// ═══════════════════════════════════════════════════════════════

export const errorService = {
  // { courseSlug, topicId?, unreviewed?, frequent? }
  getAllErrors: async (options) => (await api.get('/errors', { params: options })).data,
  getErrorById: async (id) => (await api.get(`/errors/${id}`)).data,
  updateAnalysis: async (id, analysis) => (await api.patch(`/errors/${id}/analysis`, analysis)).data,
  markAsReviewed: async (id) => { await api.post(`/errors/${id}/reviewed`); },
  markAsMastered: async (id, mastered = true) => { await api.post(`/errors/${id}/mastered`, { mastered }); },
  deleteError: async (id) => { await api.delete(`/errors/${id}`); },
  countErrors: async (topicId, courseSlug) => {
    const params = { courseSlug };
    if (topicId) params.topicId = topicId;
    return (await api.get('/errors/count', { params })).data;
  },
};

// ═══════════════════════════════════════════════════════════════
// MEDIA API (Videos & Audios)
// ═══════════════════════════════════════════════════════════════

export const mediaService = {
  // Upload video for topic
  uploadTopicVideo: async (file, topicId) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/media/upload/topic/${topicId}/video`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Upload audio for topic
  uploadTopicAudio: async (file, topicId) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/media/upload/topic/${topicId}/audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Get video URL
  getVideoUrl: (path) => {
    return `${API_BASE_URL}/media/video/${path}`;
  },

  // Get audio URL
  getAudioUrl: (path) => {
    return `${API_BASE_URL}/media/audio/${path}`;
  },
};

// ═══════════════════════════════════════════════════════════════
// PDF API
// ═══════════════════════════════════════════════════════════════

export const pdfService = {
  // Upload PDF
  uploadPdf: async (file, subfolder = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (subfolder) {
      formData.append('subfolder', subfolder);
    }
    const response = await api.post('/pdfs/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Upload PDF for topic (with optional course)
  uploadTopicPdf: async (file, topicId, courseSlug = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = courseSlug ? { courseSlug } : {};
    const response = await api.post(`/pdfs/upload/topic/${topicId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
    return response.data;
  },

  // Upload PDF for lesson
  uploadLessonPdf: async (file, lessonId, topicId = null, courseSlug = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (topicId) {
      formData.append('topicId', topicId);
    }
    if (courseSlug) {
      formData.append('courseSlug', courseSlug);
    }
    const response = await api.post(`/pdfs/upload/lesson/${lessonId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Upload PDF for course
  uploadCoursePdf: async (file, courseSlug) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/pdfs/upload/course/${courseSlug}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Get PDF URL
  getPdfUrl: (path) => {
    return `${API_BASE_URL}/pdfs/${path}`;
  },

  // Get PDF URL for course
  getCoursePdfUrl: (courseSlug, path) => {
    return `${API_BASE_URL}/pdfs/courses/${courseSlug}/${path}`;
  },
};

// ═══════════════════════════════════════════════════════════════
// PROGRESS API
// ═══════════════════════════════════════════════════════════════

export const progressService = {
  toggleProgress: async (lessonId, courseSlug) => {
    const response = await api.post(`/progress/${lessonId}/toggle`, null, { params: { courseSlug } });
    return response.data;
  },

  getProgress: async (lessonId, courseSlug) => {
    const response = await api.get(`/progress/${lessonId}`, { params: { courseSlug } });
    return response.data;
  },

  getProgressByCourse: async (courseSlug) => {
    const response = await api.get(`/progress/course/${courseSlug}`);
    return response.data;
  },

  getCompletedByCourse: async (courseSlug) => {
    const response = await api.get(`/progress/course/${courseSlug}/completed`);
    return response.data;
  },

  countCompletedByCourse: async (courseSlug) => {
    const response = await api.get(`/progress/course/${courseSlug}/count`);
    return response.data;
  },

  recordView: async (lessonId, courseSlug) => {
    await api.post(`/progress/${lessonId}/view`, null, { params: { courseSlug } });
  },

  saveNotes: async (lessonId, courseSlug, notes) =>
    (await api.put(`/progress/${lessonId}/notes`, { notes }, { params: { courseSlug } })).data,
};

// ═══════════════════════════════════════════════════════════════
// SEARCH API
// ═══════════════════════════════════════════════════════════════

export const searchService = {
  search: async (query, courseSlug = null) => {
    const params = { q: query };
    if (courseSlug) params.courseSlug = courseSlug;
    const response = await api.get('/search', { params });
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════
// STUDY TASKS API
// ═══════════════════════════════════════════════════════════════

export const taskService = {
  getTasksByTopic: async (topicId) => {
    const response = await api.get(`/tasks/topic/${topicId}`);
    return response.data;
  },

  getTasksByCourse: async (courseSlug, status = null) => {
    const params = status ? { status } : {};
    const response = await api.get(`/tasks/course/${courseSlug}`, { params });
    return response.data;
  },

  getTaskById: async (id) => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },

  createTask: async (task) => {
    const response = await api.post('/tasks', task);
    return response.data;
  },

  updateTask: async (id, task) => {
    const response = await api.put(`/tasks/${id}`, task);
    return response.data;
  },

  updateStatus: async (id, status) => {
    const response = await api.patch(`/tasks/${id}/status`, { status });
    return response.data;
  },

  deleteTask: async (id) => {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  },

  countByCourse: async (courseSlug, status = null) => {
    const params = status ? { status } : {};
    const response = await api.get(`/tasks/course/${courseSlug}/count`, { params });
    return response.data;
  },
};

// ═══════════════════════════════════════════════════════════════
// FLASHCARD PROGRESS API
// ═══════════════════════════════════════════════════════════════

export const flashcardProgressService = {
  reviewCard: async (topicId, cardIndex, courseSlug, quality) => {
    const response = await api.post('/flashcard-progress/review', {
      topicId, cardIndex, courseSlug, quality,
    });
    return response.data;
  },

  getProgressByTopic: async (topicId, courseSlug) => {
    const response = await api.get(`/flashcard-progress/topic/${topicId}`, { params: { courseSlug } });
    return response.data;
  },

  getDueCards: async (courseSlug) => {
    const response = await api.get('/flashcard-progress/due', { params: { courseSlug } });
    return response.data;
  },

  countDueCards: async (courseSlug) => {
    const response = await api.get('/flashcard-progress/due/count', { params: { courseSlug } });
    return response.data;
  },

  getDueCardsByTopic: async (topicId, courseSlug) => {
    const response = await api.get(`/flashcard-progress/due/topic/${topicId}`, { params: { courseSlug } });
    return response.data;
  },

  setMastered: async (topicId, cardIndex, courseSlug, mastered) =>
    (await api.post('/flashcard-progress/mastered', { topicId, cardIndex, courseSlug, mastered })).data,

  // Fila de revisão diária do curso: { due, fresh, totalCards, masteredCards }
  getReviewQueue: async (courseSlug, newLimit = 10) =>
    (await api.get('/flashcard-progress/review-queue', { params: { courseSlug, newLimit } })).data,
};

// ═══════════════════════════════════════════════════════════════
// DASHBOARD API
// ═══════════════════════════════════════════════════════════════

export const dashboardService = {
  getDashboard: async (courseSlug) => {
    const response = await api.get(`/dashboard/${courseSlug}`);
    return response.data;
  },
};

export default api;
